import { useRef, type ChangeEvent } from 'react'
import {
    Database,
    DatabaseZap,
    FileText,
    Layers,
    Loader2,
    Lock,
    Plus,
} from 'lucide-react'
import type { KnowledgeCorpus, KnowledgeCorpusDocument } from '~/api/types'
import {
    STATUS_LABEL,
    STATUS_TONE,
    type DocumentRow,
} from '~/components/knowledge/UploadManagementSection'
import { StatCard } from '~/components/StatCard'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { SearchInput } from '~/components/ui/search-input'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'

// 코퍼스 소스 유형 → 배지 의미색(없는 키는 중립). label은 백엔드 응답(bySource)을 우선한다.
// (지식베이스 페이지의 드로어와 코퍼스 표가 공유하므로 여기서 export 한다.)
export const SOURCE_TONE: Record<string, BadgeTone> = {
    catalog: 'accent',
    regulation: 'warning',
    manual: 'info',
}

// ───────── 색인된 코퍼스(Qdrant) — 청크 기준 실제 RAG 코퍼스 ─────────
// 요약 KPI + 소스칩 + 문서 표(제목 필터는 클라이언트) + 문서 추가(업로드 등록) +
// 문서 클릭 → 청크 드릴다운 트리거(onSelectDoc). 코퍼스는 운영 CLI(datapipe)로 적재되므로
// 업로드 표가 비어도 위에 표시된다. available=false는 에러가 아니라 안내로 다룬다.
export function CorpusSection({
    corpus,
    corpusLoading,
    corpusQ,
    onCorpusQChange,
    uploading,
    onUploadFile,
    onSelectDoc,
    sourceLabel,
    docsById,
}: {
    corpus: KnowledgeCorpus | null
    corpusLoading: boolean
    corpusQ: string
    onCorpusQChange: (value: string) => void
    uploading: boolean
    onUploadFile: (e: ChangeEvent<HTMLInputElement>) => void
    onSelectDoc: (doc: KnowledgeCorpusDocument) => void
    sourceLabel: (key: string) => string
    // 업로드 파이프라인 상태 조인: documentId → documents 행. 매칭되면 인덱싱 상태를,
    // 매칭 안 되면(카탈로그·CLI 적재) 'CLI 적재'로 표기한다. 파이프라인 관리는 「업로드 관리」에서.
    docsById: Map<string, DocumentRow>
}) {
    const fileInputRef = useRef<HTMLInputElement>(null)

    // 코퍼스 문서 제목 클라이언트 필터.
    const corpusQuery = corpusQ.trim().toLowerCase()
    const corpusDocs = (corpus?.documents ?? []).filter((d) =>
        corpusQuery ? d.title.toLowerCase().includes(corpusQuery) : true
    )

    return (
        <section className='mb-8'>
            <div className='mb-3 flex items-start justify-between gap-3'>
                <div>
                    <div className='admin-eyebrow'>색인된 코퍼스</div>
                    <p className='text-muted-foreground mt-0.5 text-[13px]'>
                        벡터 스토어(Qdrant)에 적재된 RAG 코퍼스 · 검색·상담에
                        실제로 쓰이는 청크입니다. 문서를 클릭하면 청크를 펼쳐
                        봅니다.
                    </p>
                </div>
                <div className='shrink-0'>
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        title='PDF·Markdown·Excel 원본 파일을 선택해 문서로 등록해요. 워커가 인덱싱하면 코퍼스에 반영됩니다.'>
                        {uploading ? (
                            <Loader2 className='animate-spin' />
                        ) : (
                            <Plus />
                        )}
                        {uploading ? '추가하는 중…' : '문서 추가'}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.pdf,.md,.markdown,.xlsx,application/pdf,text/markdown,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        className='hidden'
                        onChange={onUploadFile}
                    />
                </div>
            </div>

            {corpusLoading ? (
                <div className='space-y-4'>
                    <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className='h-20' />
                        ))}
                    </div>
                    <Skeleton className='h-9' />
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className='h-9' />
                    ))}
                </div>
            ) : !corpus || !corpus.available ? (
                <Card>
                    <EmptyState
                        icon={DatabaseZap}
                        title='벡터 스토어(Qdrant)에 연결할 수 없어요'
                        description='색인된 코퍼스를 불러오지 못했어요. 벡터 스토어 연결 상태를 확인한 뒤 새로고침해 주세요.'
                    />
                </Card>
            ) : (
                <>
                    {/* 코퍼스 요약 KPI: 총 청크 · 소스 종류 · 코퍼스 문서 */}
                    <div className='mb-3 grid grid-cols-2 gap-4 lg:grid-cols-3'>
                        <StatCard
                            label='총 청크'
                            value={corpus.totalChunks.toLocaleString()}
                            icon={Database}
                            tone='primary'
                            hint='색인된 임베딩 청크'
                        />
                        <StatCard
                            label='소스 종류'
                            value={corpus.bySource.length}
                            icon={Layers}
                            tone='info'
                            hint='카탈로그 · 고시 · 매뉴얼 등'
                        />
                        <StatCard
                            label='코퍼스 문서'
                            value={corpus.documents.length.toLocaleString()}
                            icon={FileText}
                            hint='청크가 적재된 원문 수'
                        />
                    </div>

                    {/* 소스별 배지: 문서·청크 수를 칩으로 고밀도 표시 */}
                    {corpus.bySource.length > 0 ? (
                        <div className='mb-4 flex flex-wrap gap-1.5'>
                            {corpus.bySource.map((s) => (
                                <Badge
                                    key={s.key}
                                    tone={SOURCE_TONE[s.key] ?? 'neutral'}>
                                    {s.label} · {s.docCount}문서 ·{' '}
                                    {s.chunkCount.toLocaleString()}청크
                                </Badge>
                            ))}
                        </div>
                    ) : null}

                    {/* 제목 필터(클라이언트) */}
                    <div className='mb-3'>
                        <SearchInput
                            className='w-72'
                            value={corpusQ}
                            onChange={onCorpusQChange}
                            placeholder='코퍼스 제목 검색'
                        />
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>제목</TableHead>
                                <TableHead>도메인</TableHead>
                                <TableHead>소스</TableHead>
                                <TableHead>상태</TableHead>
                                <TableHead className='text-right'>
                                    청크수
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {corpusDocs.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className='text-muted-foreground py-8 text-center'>
                                        {corpusQuery
                                            ? '검색 결과가 없어요.'
                                            : '색인된 코퍼스 문서가 없어요.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                corpusDocs.map((d) => {
                                    // 파이프라인 상태 조인: documentId로 documents 행을 찾는다.
                                    // 매칭되면 인덱싱 상태(+ 잠금), 없으면 CLI/카탈로그 직접 적재.
                                    const doc = docsById.get(d.documentId)
                                    return (
                                        <TableRow
                                            key={d.documentId}
                                            className='cursor-pointer'
                                            onClick={() => onSelectDoc(d)}>
                                            <TableCell>{d.title}</TableCell>
                                            <TableCell>{d.domain}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    tone={
                                                        SOURCE_TONE[
                                                            d.sourceType
                                                        ] ?? 'neutral'
                                                    }>
                                                    {sourceLabel(d.sourceType)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {doc ? (
                                                    <span className='flex items-center gap-1.5'>
                                                        <Badge
                                                            tone={
                                                                STATUS_TONE[
                                                                    doc.status
                                                                ] ?? 'neutral'
                                                            }>
                                                            {STATUS_LABEL[
                                                                doc.status
                                                            ] ?? doc.status}
                                                        </Badge>
                                                        {doc.curationLocked ? (
                                                            <Badge
                                                                tone='warning'
                                                                title='수동 관리 잠금 · 재색인이 이 문서를 건너뜁니다'>
                                                                <Lock className='size-3' />
                                                                잠금
                                                            </Badge>
                                                        ) : null}
                                                    </span>
                                                ) : (
                                                    <Badge
                                                        tone='neutral'
                                                        title='업로드 파이프라인 외 · 운영 CLI/직접 적재된 코퍼스 (관리는 카탈로그/CLI)'>
                                                        CLI 적재
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className='tnum text-right'>
                                                {d.chunkCount.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </>
            )}
        </section>
    )
}
